import React from 'react';
import { Payment, Site } from '@luckycart/xpn-types';
import BankIcon from './images/bank.svg';
import PaypalIcon from './images/paypal.png';
import { ScreenReaderOnly } from './RewardForm.style';
import { PaymentMethodButtonStyled, PaymentMethodGroupStyled } from './CashRewardForm.style';
import { validateBic, validateIban, validatePaypalField, validateRequired } from '../../utils/validateForm';
import { AccessibleInputField } from './AccessibleInputField';
import { getExpectedFormatHint, getIbanExpectedFormat } from './utils/expectedFormat';

interface Props {
  config: Payment;
  isBank: boolean;
  setIsBank: React.Dispatch<React.SetStateAction<boolean>>;
  siteConfig: Site;
}

export const CashRewardForm: React.FunctionComponent<Props> = ({ config, isBank, setIsBank, siteConfig }: Props) => {
  const { texts: formWording } = config;
  const paymentMethodGroupLabelId = 'payment-method-group-label';
  const bankButtonRef = React.useRef<HTMLButtonElement>(null);
  const paypalButtonRef = React.useRef<HTMLButtonElement>(null);
  const ibanExpectedFormat = getIbanExpectedFormat(siteConfig.countryCode);
  const ibanFormatHint = getExpectedFormatHint(formWording.expectedFormat.prefix, ibanExpectedFormat);
  const bicFormatHint = getExpectedFormatHint(formWording.expectedFormat.prefix, formWording.expectedFormat.bic);
  const paypalFormatHint = getExpectedFormatHint(formWording.expectedFormat.prefix, formWording.expectedFormat.paypal);

  const selectPaymentMethod = (nextIsBank: boolean) => {
    setIsBank(nextIsBank);
    const button = nextIsBank
      ? bankButtonRef.current
      : paypalButtonRef.current;
    button?.focus();
  };

  // Accessibility: a radio group is navigated with arrow keys.
  // Arrows move to the other payment method and select it. With two options every arrow wraps.
  // Enter and Space stay the button's native behavior.
  const handlePaymentMethodKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const moveToNext = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const moveToPrevious = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!moveToNext && !moveToPrevious) {
      return;
    }
    event.preventDefault();
    selectPaymentMethod(!isBank);
  };

  return (
    <>
      <AccessibleInputField
        name="firstName"
        label={formWording.first}
        required
        validate={(value) => validateRequired(value, formWording.requiredMessage)}
        autoComplete="given-name"
        placeholder={formWording.placeholders.firstName}
      />
      <AccessibleInputField
        name="lastName"
        label={formWording.last}
        required
        validate={(value) => validateRequired(value, formWording.requiredMessage)}
        autoComplete="family-name"
        placeholder={formWording.placeholders.lastName}
      />
      <p style={{ margin: '0 0 1rem 0' }}>{formWording.notice.paymentMethodChange}</p>
      <ScreenReaderOnly id={paymentMethodGroupLabelId}>{formWording.notice.paymentMethodGroupLabel}</ScreenReaderOnly>
      <PaymentMethodGroupStyled role="radiogroup" aria-labelledby={paymentMethodGroupLabelId}>
        <PaymentMethodButtonStyled
          ref={bankButtonRef}
          type="button"
          role="radio"
          aria-checked={isBank}
          tabIndex={isBank
            ? 0
            : -1}
          onClick={() => selectPaymentMethod(true)}
          onKeyDown={handlePaymentMethodKeyDown}
        >
          <img src={BankIcon} alt="" />
          {formWording.iban}
        </PaymentMethodButtonStyled>
        <PaymentMethodButtonStyled
          ref={paypalButtonRef}
          type="button"
          role="radio"
          aria-checked={!isBank}
          tabIndex={isBank
            ? -1
            : 0}
          onClick={() => selectPaymentMethod(false)}
          onKeyDown={handlePaymentMethodKeyDown}
        >
          <img src={PaypalIcon} alt="" />
          {formWording.paypal}
        </PaymentMethodButtonStyled>
      </PaymentMethodGroupStyled>
      {isBank
        ? (
          <>
            <AccessibleInputField
              name="iban"
              label={formWording.number}
              required
              validate={(value) => validateIban(value, formWording, siteConfig.countryCode, formWording.requiredMessage, formWording.invalidMessage)}
              hidden={!isBank}
              hint={ibanFormatHint}
              placeholder={ibanExpectedFormat}
              transformValue={(value) => value.toUpperCase()}
            />
            <AccessibleInputField
              name="bic"
              label={formWording.bic}
              required
              validate={(value) => validateBic(value, formWording, siteConfig.countryCode, formWording.requiredMessage, formWording.invalidMessage)}
              hidden={!isBank}
              inputStyle={{ width: '45%' }}
              hint={bicFormatHint}
              placeholder={formWording.expectedFormat.bic}
              transformValue={(value) => value.toUpperCase()}
            />
          </>
        )
        : (
          <AccessibleInputField
            name="email"
            label={formWording.email}
            required
            validate={(value) => (isBank
              ? undefined
              : validatePaypalField(value, formWording.requiredMessage, formWording.invalidMessage))}
            hidden={isBank}
            hint={paypalFormatHint}
            autoComplete="email"
            placeholder={formWording.expectedFormat.paypal}
          />
        )}
    </>
  );
};
